import api from './api'
import type { ApiResponse, PaginatedResponse } from '../types'

// ================================================================================
// CMS CONTENT SERVICE — knowledge-article review workflow
// Draft → Pending Review → Approved → Published → Archived
// All endpoints are admin-only (ADMIN / SUPER_ADMIN) on the backend.
// ================================================================================

export type ArticleStatus =
  | 'DRAFT'
  | 'PENDING_REVIEW'
  | 'APPROVED'
  | 'PUBLISHED'
  | 'ARCHIVED'

/** `null` = legacy article created before the workflow existed. */
export type ArticleStatusFilter = ArticleStatus | 'ALL'

export interface CmsArticle {
  id: number
  title: string
  slug: string
  excerpt?: string
  content?: string
  featuredImage?: string
  tags?: string[]
  viewCount?: number
  likeCount?: number
  isFeatured?: boolean
  isPublished?: boolean
  publishedAt?: string
  /** CMS workflow state; null for legacy articles. */
  status?: ArticleStatus | null
  reviewedBy?: number | null
  reviewedAt?: string | null
  reviewNotes?: string | null
  createdAt?: string
  updatedAt?: string
  author?: { id?: number; firstName?: string; lastName?: string; username?: string }
  category?: { id?: number; name?: string; slug?: string }
}

export interface CmsArticlePage {
  content: CmsArticle[]
  totalElements: number
  totalPages: number
  number: number
  size: number
}

export interface CmsReviewRequest {
  notes?: string
}

export const STATUS_FILTERS: ArticleStatusFilter[] = [
  'ALL',
  'DRAFT',
  'PENDING_REVIEW',
  'APPROVED',
  'PUBLISHED',
  'ARCHIVED',
]

/** Human-readable label for a status (handles the legacy null case). */
export function statusLabel(status?: ArticleStatus | null): string {
  switch (status) {
    case 'DRAFT':
      return 'Draft'
    case 'PENDING_REVIEW':
      return 'Pending Review'
    case 'APPROVED':
      return 'Approved'
    case 'PUBLISHED':
      return 'Published'
    case 'ARCHIVED':
      return 'Archived'
    default:
      return 'Legacy (no workflow)'
  }
}

/**
 * List all articles for admin oversight, optionally filtered by status.
 * GET /admin/articles?status=&page=&size=
 */
export async function getArticlesForAdmin(params: {
  status?: ArticleStatusFilter
  page?: number
  size?: number
}): Promise<CmsArticlePage> {
  const qs = new URLSearchParams()
  qs.set('status', params.status ?? 'ALL')
  qs.set('page', String(params.page ?? 0))
  qs.set('size', String(params.size ?? 20))
  const res = await api.get<ApiResponse<PaginatedResponse<CmsArticle>>>(
    `/admin/articles?${qs.toString()}`
  )
  return res.data.data
}

/** POST /admin/articles/:id/approve */
export async function approveArticle(id: number, body?: CmsReviewRequest): Promise<CmsArticle> {
  const res = await api.post<ApiResponse<CmsArticle>>(`/admin/articles/${id}/approve`, body ?? {})
  return res.data.data
}

/** POST /admin/articles/:id/publish — make an approved article public. */
export async function publishArticle(id: number): Promise<CmsArticle> {
  const res = await api.post<ApiResponse<CmsArticle>>(`/admin/articles/${id}/publish`)
  return res.data.data
}

/** POST /admin/articles/:id/reject — return to DRAFT with optional feedback. */
export async function rejectArticle(id: number, body?: CmsReviewRequest): Promise<CmsArticle> {
  const res = await api.post<ApiResponse<CmsArticle>>(`/admin/articles/${id}/reject`, body ?? {})
  return res.data.data
}

/** POST /admin/articles/:id/archive — remove from the public portal. */
export async function archiveArticle(id: number): Promise<CmsArticle> {
  const res = await api.post<ApiResponse<CmsArticle>>(`/admin/articles/${id}/archive`)
  return res.data.data
}

/** POST /admin/articles/:id/restore — restore an archived article to DRAFT. */
export async function restoreArticle(id: number): Promise<CmsArticle> {
  const res = await api.post<ApiResponse<CmsArticle>>(`/admin/articles/${id}/restore`)
  return res.data.data
}

/** Author: POST /knowledge/articles/:id/submit-review — DRAFT → PENDING_REVIEW. */
export async function submitArticleForReview(id: number): Promise<void> {
  await api.post<ApiResponse<void>>(`/knowledge/articles/${id}/submit-review`)
}